import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { BellRing, Check, ListChecks, Loader2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { apiErrorDescription } from '@/services/core/apiError'
import {
  DELIVERY_METHODS,
  NotificationPreferencesApi,
  OVERRIDABLE_MESSAGES,
  SUBSCRIPTION_TYPES,
  type DeliveryMethod,
  type NotificationPreferences,
  type NotificationSubscription,
  type SubscriptionType,
} from '@/services/notifications/notificationPreferencesApi'
import { NOTIFICATION_SEVERITIES, type NotificationSeverity } from '@/services/notifications/notificationsApi'
import { AppShell } from '../../components/AppShell'
import { BadgeToggleGroup } from '../../components/BadgeToggleGroup'
import { FormSkeleton } from '../../components/FormSkeleton'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useCurrentUser } from '../../contexts/UserContext'
import { useNetwork } from '../../contexts/NetworkContext'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'

const SEVERITY_BADGES: Record<NotificationSeverity, 'gray' | 'warning' | 'blue' | 'red'> = {
  INFO: 'gray',
  WARNING: 'warning',
  IMPORTANT: 'blue',
  ERROR: 'red',
}

interface SubscriptionRow {
  subscribed: boolean
  severityLevel: NotificationSeverity | ''
  deliveryMethods: DeliveryMethod[]
}

interface FormValues {
  subscriptions: Record<SubscriptionType, SubscriptionRow>
  overrides: Record<string, DeliveryMethod[]>
}

function toFormValues(prefs: NotificationPreferences | undefined, messageIds: string[]): FormValues {
  const subscriptions = Object.fromEntries(
    SUBSCRIPTION_TYPES.map((type) => {
      const sub = prefs?.subscriptions[type]
      const methods = sub?.deliveryMethods ?? []
      return [
        type,
        { subscribed: methods.length > 0, severityLevel: sub?.severityLevel ?? '', deliveryMethods: methods },
      ]
    })
  ) as Record<SubscriptionType, SubscriptionRow>
  const overrides = Object.fromEntries(messageIds.map((id) => [id, prefs?.overrides[id] ?? []]))
  return { subscriptions, overrides }
}

function isRowInvalid(row: SubscriptionRow): boolean {
  return row.subscribed && (!row.severityLevel || row.deliveryMethods.length === 0)
}

export function NotificationPreferencesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUser = useCurrentUser()
  const { currentNetwork } = useNetwork()
  const queryKey = ['notificationPreferences', currentNetwork?.tagId, currentUser?.id]

  const messages = useMemo(
    () =>
      currentNetwork?.suiPlatform
        ? [...OVERRIDABLE_MESSAGES, { id: 'NEW_SIGNUP_USER', severity: 'INFO' as const }]
        : OVERRIDABLE_MESSAGES,
    [currentNetwork?.suiPlatform]
  )
  const messageIds = useMemo(() => messages.map((m) => m.id), [messages])

  const { data: prefs, isLoading } = useQuery({
    queryKey,
    queryFn: () => NotificationPreferencesApi.get(currentUser!.id),
    enabled: !!currentUser && !!currentNetwork,
  })

  const [defaults, setDefaults] = useState<FormValues>(() => toFormValues(undefined, messageIds))
  const form = useForm({
    defaultValues: defaults,
    onSubmit: ({ value }) => mutation.mutate(value),
  })
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  useEffect(() => {
    if (!prefs) return
    const values = toFormValues(prefs, messageIds)
    setDefaults(values)
    form.reset(values)
    markClean(values)
  }, [prefs, messageIds])

  const mutation = useMutation({
    mutationFn: async (value: FormValues) => {
      if (!currentUser || !prefs) throw new Error('No user')
      // Unsubscribed types are left out: the API wants a severity on every subscription it gets.
      const subscriptions: NotificationPreferences['subscriptions'] = {}
      for (const type of SUBSCRIPTION_TYPES) {
        const row = value.subscriptions[type]
        if (!row.subscribed) continue
        subscriptions[type] = {
          severityLevel: row.severityLevel as NotificationSeverity,
          deliveryMethods: row.deliveryMethods,
        } satisfies NotificationSubscription
      }
      await NotificationPreferencesApi.save(currentUser.id, {
        id: prefs.id,
        subscriptions,
        overrides: value.overrides,
      })
      return value
    },
    onSuccess: (value) => {
      markClean(value)
      queryClient.invalidateQueries({ queryKey })
      // What reaches the bell may have changed.
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      toast.success(t('notificationPreferences.savedTitle'), { duration: 3000 })
    },
    onError: (error) => {
      toast.error(t('notificationPreferences.saveFailedTitle'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const crumbs = [{ label: t('nav.admin'), href: '/admin' }, { label: t('nav.notificationPreferences') }]
  if (isLoading) {
    return (
      <AppShell crumbs={crumbs}>
        <FormSkeleton />
      </AppShell>
    )
  }

  const methodOptions = DELIVERY_METHODS.map((method) => ({
    value: method,
    label: t(`notificationPreferences.deliveryMethod.${method}`),
  }))

  return (
    <AppShell crumbs={crumbs}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('nav.notificationPreferences')}</h1>
            <p>{t('notificationPreferences.subtitle')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-[14px]">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={BellRing} />
                  {t('notificationPreferences.subscriptions')}
                </CardTitle>
                <CardDescription>{t('notificationPreferences.subscriptionsHelp')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('notificationPreferences.objectType')}</TableHead>
                      <TableHead>{t('notificationPreferences.subscribed')}</TableHead>
                      <TableHead>{t('notificationPreferences.severityFrom')}</TableHead>
                      <TableHead>{t('notificationPreferences.deliveredBy')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {SUBSCRIPTION_TYPES.map((type) => (
                      <form.Field key={type} name={`subscriptions.${type}`}>
                        {(field) => {
                          const row = field.state.value
                          const typeLabel = t(`notificationPreferences.types.${type}`)
                          const set = (patch: Partial<SubscriptionRow>) =>
                            field.handleChange({ ...row, ...patch })
                          return (
                            <TableRow>
                              <TableCell className="font-medium">{typeLabel}</TableCell>
                              <TableCell>
                                <Switch
                                  checked={row.subscribed}
                                  aria-label={t('notificationPreferences.subscribeTo', { type: typeLabel })}
                                  onCheckedChange={(subscribed) =>
                                    set(
                                      subscribed
                                        ? {
                                            subscribed,
                                            severityLevel: row.severityLevel || 'INFO',
                                            deliveryMethods: row.deliveryMethods.length
                                              ? row.deliveryMethods
                                              : ['UI'],
                                          }
                                        : { subscribed, deliveryMethods: [] }
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Select
                                  label={t('notificationPreferences.severityFrom')}
                                  value={row.severityLevel || undefined}
                                  disabled={!row.subscribed}
                                  onValueChange={(v) => set({ severityLevel: v as NotificationSeverity })}
                                >
                                  <SelectTrigger className="w-40" aria-invalid={isRowInvalid(row) && !row.severityLevel}>
                                    <SelectValue placeholder={t('notificationPreferences.notApplicable')}>
                                      {row.severityLevel
                                        ? t(`notificationPreferences.severity.${row.severityLevel}`)
                                        : null}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {NOTIFICATION_SEVERITIES.map((level) => (
                                      <SelectItem key={level} value={level}>
                                        {t(`notificationPreferences.severity.${level}`)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <BadgeToggleGroup
                                  options={methodOptions}
                                  value={row.deliveryMethods}
                                  disabled={!row.subscribed}
                                  onChange={(methods) => set({ deliveryMethods: methods as DeliveryMethod[] })}
                                />
                                {isRowInvalid(row) && row.deliveryMethods.length === 0 && (
                                  <p className="mt-1 text-xs text-destructive">
                                    {t('notificationPreferences.methodRequired')}
                                  </p>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        }}
                      </form.Field>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={ListChecks} />
                  {t('notificationPreferences.overrides')}
                </CardTitle>
                <CardDescription>{t('notificationPreferences.overridesHelp')}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('notificationPreferences.notification')}</TableHead>
                      <TableHead>{t('notificationPreferences.severityLabel')}</TableHead>
                      <TableHead>{t('notificationPreferences.deliveredBy')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {messages.map((message) => (
                      <form.Field key={message.id} name={`overrides.${message.id}`}>
                        {(field) => (
                          <TableRow>
                            <TableCell>{t(`notificationPreferences.messages.${message.id}`)}</TableCell>
                            <TableCell>
                              <Badge variant={SEVERITY_BADGES[message.severity]}>
                                {t(`notificationPreferences.severity.${message.severity}`)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <BadgeToggleGroup
                                options={methodOptions}
                                value={field.state.value ?? []}
                                onChange={(methods) => field.handleChange(methods as DeliveryMethod[])}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </form.Field>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

        <form.Subscribe selector={(state) => SUBSCRIPTION_TYPES.some((type) => isRowInvalid(state.values.subscriptions[type]))}>
          {(invalid) => (
            <div className={`detail-savebar${isDirty || mutation.isPending ? ' is-visible' : ''}`}>
              <div className="detail-savebar__left">
                {mutation.isPending && (
                  <>
                    <Loader2 className="animate-spin size-4" />
                    {t('common.saving')}
                  </>
                )}
              </div>
              <div className="detail-savebar__actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={mutation.isPending}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit" loading={mutation.isPending} disabled={invalid}>
                  <Check className="size-4" /> {t('common.save')}
                </Button>
              </div>
            </div>
          )}
        </form.Subscribe>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}
